import { useState } from 'react';
import { formatCurrency, formatCurrencyWhole } from '../../utils/api';
import Icon from '../../components/Icon';
import { PRELAUNCH_NOTE, isPrelaunch, monthLabelOf, monthNameOf, spanLabelOf } from './businessMonth';

// The headline figures of BE העסק, and the small parts they are built from.
//
// Its own module because two screens show the same tiles: BE העסק itself,
// over the period its picker selects, and מבט על's מצב העסק card, over last
// month. One component for both is what keeps them from drifting — a tile that
// reads "הכנסות" on one screen and computes something else on the other is the
// kind of disagreement nobody notices until it is quoted.

/**
 * "vs the previous period", as a direction and a percentage.
 *
 * The previous period is the same number of months immediately before the span,
 * so the comparison is like-for-like however long the period is. Two cases get
 * words instead of a number, because a percentage would be a lie in both: a
 * previous period of zero has no percentage (∞), and an unchanged one should
 * not read as "+0%" — that invites the eye to look for a trend that isn't there.
 */
export function DeltaDetail({ now, prev, prevLabel, mode = 'percent', formatValue, prevPrelaunch }) {
  const show = formatValue || String;
  // The comparison window lies wholly before go-live: there is no previous
  // period, so no line at all (owner, 14/09/2026) — the note at the foot of the
  // page already says why, and "אין נתוני השוואה" would suggest missing data.
  if (prevPrelaunch) return null;
  if (!prev) {
    return <span className="cc-metric-detail">{now ? 'אין נתוני השוואה' : '—'}</span>;
  }
  if (now === prev) return <span className="cc-metric-detail">ללא שינוי לעומת {prevLabel}</span>;
  const up = now > prev;
  // A count of five customers that was four is "+1", not "25%": on figures this
  // small a percentage is a bigger number than the thing it describes, and it
  // reads as a swing rather than as one signup.
  const amount = mode === 'count'
    ? `${up ? '+' : '−'}${Math.abs(now - prev)}`
    : `${Math.round(Math.abs((now - prev) / prev) * 100)}%`;
  return (
    <span className={`cc-metric-detail cc-delta ${up ? 'is-up' : 'is-down'}`}
      title={`${prevLabel}: ${show(prev)}`}>
      <Icon name={up ? 'arrow-up' : 'arrow-down'} size={12} />
      {amount} לעומת {prevLabel}
    </span>
  );
}

/** One headline figure. Deliberately NOT .cc-state — those are buttons that
 *  route somewhere, and every one of these is a terminal fact. */
export function Metric({ label, value, detail, tone, title, extra, icon, menu }) {
  return (
    <div className={`cc-metric${tone ? ` is-${tone}` : ''}${menu ? ' has-menu' : ''}`} title={title}>
      {menu && <div className="cc-metric-menu">{menu}</div>}
      <span className="cc-metric-value">{value}</span>
      <span className="cc-metric-label">
        {icon && <Icon name={icon} size={14} />}
        {label}
      </span>
      {extra}
      {detail && <span className="cc-metric-detail">{detail}</span>}
    </div>
  );
}

/**
 * How much of the customer count actually pays, as a split bar under the count.
 *
 * A ₪0 fee means a pilot or a free year, so "סה״כ לקוחות" alone overstates the
 * paying base. A bar rather than a second number: the question is the SHARE,
 * and a filled track answers it before the reader has parsed a digit.
 */
function PayingSplit({ paying, free, total }) {
  if (!total) return null;
  const unset = Math.max(total - paying - free, 0);
  return (
    <div className="cc-paying">
      <div className="cc-paying-track" role="img"
        aria-label={`${paying} משלמים מתוך ${total} לקוחות`}>
        <span className="cc-paying-fill" style={{ width: `${(paying / total) * 100}%` }} />
      </div>
      <div className="cc-paying-legend">
        <span><i className="cc-paying-dot is-paying" /><b>{paying}</b> משלמים</span>
        {free > 0 && <span><i className="cc-paying-dot is-free" /><b>{free}</b> חינם</span>}
        {unset > 0 && <span><i className="cc-paying-dot is-unset" /><b>{unset}</b> לא הוזן</span>}
      </div>
    </div>
  );
}

/**
 * The card's ⋮ — how this card's numbers are worked out.
 *
 * Same construction and the same wording standard as /analysis's card menus
 * (`page-menu` / `page-menu-panel`, the חשוב לדעת glossary on the target card):
 * a trigger in the header corner, a backdrop that dismisses, and prose at
 * 12.5px rather than a list of actions. Every figure on this screen is derived
 * — an estimate from a standing fee, a count at the end of a span, a ratio over
 * a period — and a derived number nobody can check is a number nobody trusts.
 */
export function CardHelp({ title, children, heading = 'איך מחושב', action }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="page-menu cc-cardhelp">
      <button
        type="button"
        className="page-menu-trigger"
        aria-label={`איך מחושב — ${title}`}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >⋮</button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="page-menu-panel cc-cardhelp-panel" role="dialog" aria-label={`איך מחושב — ${title}`}>
            <div className="cc-cardhelp-title">{heading}</div>
            {children}
            {/* One action at the foot of the explanation — for a figure whose
                input is ours to set (the headcount), the place you learn how
                it is worked out is the place you correct what goes into it. */}
            {action && (
              <button type="button" className="cc-cardhelp-action"
                onClick={() => { setOpen(false); action.run(); }}>
                {action.icon && <Icon name={action.icon} size={15} />} {action.label}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** One explanatory paragraph inside a CardHelp, at the /analysis help scale. */
export function HelpNote({ children }) {
  return <p className="cc-cardhelp-note">{children}</p>;
}

/** A customer's standing חיוב לחודש. "‏0.00 ₪" says nothing about which of two
 *  very different things is true, so both are named: a fee nobody has entered
 *  yet (לא הוזן), and a ₪0 fee entered on purpose — a pilot or a free year
 *  (חינם). Shared by the directory and the payment board; `suffix` is appended
 *  to a real amount only ("‏450.00 ₪ לחודש"). */
export function Fee({ amount, entered, suffix = '' }) {
  if (entered === false) {
    return <span className="cc-muted" title="עדיין לא הוזן חיוב לחודש בפרטי הלקוח">לא הוזן</span>;
  }
  if (!amount) return <span className="cc-pill" title="חיוב לחודש ‏0 ₪ — פיילוט או תקופה ללא תשלום">חינם</span>;
  return <span>{formatCurrency(amount)}{suffix}</span>;
}

/** An average headcount: whole when it is whole ("2"), one decimal when not ("2.5"). */
const headcountLabel = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * The headline tiles for one /crm payload: סה״כ לקוחות, (BE העסק only)
 * לקוחות חדשים מתוכננים, סה״כ הכנסות, צפי הכנסות for the calendar month, and
 * the star metric הכנסות לעובד.
 *
 * `onEditHeadcount` is what gives the star tile its ⋮ (the explanation and the
 * headcount editor behind it). Omitted — as on מבט על, where the whole card is a
 * link and a menu inside it would be a button inside a button — the tiles are
 * read-only. `showPlanned` adds לקוחות חדשים מתוכננים — BE העסק only.
 */
export function BusinessFigures({ data, onEditHeadcount, showPlanned = false }) {
  const summary = data.summary;
  const rpe = summary.revenue_per_employee || null;
  // BE העסק only (owner, 25/09/2026): מבט על's מצב העסק card is about LAST
  // month and is a four-column grid; a count from today does not belong in it.
  const planned = showPlanned && Array.isArray(summary.planned_customers) ? summary.planned_customers : null;
  const prevLabel = spanLabelOf(data.prev_from, data.prev_to);
  // The whole comparison window is before go-live — see DeltaDetail.
  const prevPrelaunch = isPrelaunch(data.live_from, data.prev_to);

  return (
    <div className="cc-figures">
      <Metric
        label="סה״כ לקוחות"
        value={summary.customer_count}
        extra={<PayingSplit paying={summary.paying_customer_count ?? 0}
          free={summary.free_customer_count ?? 0} total={summary.customer_count} />}
        detail={<DeltaDetail
          now={summary.customer_count}
          prev={summary.customer_count_prev}
          prevLabel={prevLabel}
          prevPrelaunch={prevPrelaunch}
          mode="count"
        />}
        title={`בסוף ${monthLabelOf(data.to)} · משלמים = חיוב לחודש מעל ‏0 ₪ בפרטי הלקוח, שולם או לא · חינם = הוזן ‏0 ₪`}
      />
      {/* Right after סה״כ לקוחות (owner, 25/09/2026): who is coming. A
          restaurant already in PEAK with a FUTURE חודש התחלה. Counted from
          today, not from the picker — said on hover, like צפי הכנסות. */}
      {planned && (
        <Metric
          tone="planned"
          icon="heart"
          label="לקוחות חדשים מתוכננים"
          value={planned.length}
          detail={planned.length
            ? `הבא: ${planned[0].name} · ${monthLabelOf(planned[0].start_month)}`
            : 'אין הצטרפויות מתוכננות'}
          title={[
            'מסעדות שחודש ההתחלה שלהן בכרטיס הלקוח אחרי החודש הנוכחי — לפי התאריך של היום, לא לפי התקופה.',
            ...planned.map(c => `${c.name} · ${monthLabelOf(c.start_month)}`
              + (c.monthly_fee_entered ? ` · ${formatCurrencyWhole(c.monthly_fee)} לחודש` : '')),
          ].join('\n')}
        />
      )}
      <Metric
        label="סה״כ הכנסות"
        value={formatCurrencyWhole(summary.expected)}
        detail={<DeltaDetail
          now={summary.expected}
          prev={summary.expected_prev}
          prevLabel={prevLabel}
          prevPrelaunch={prevPrelaunch}
          formatValue={formatCurrencyWhole}
        />}
        title={`${summary.billed_count} חיובים בטווח`}
      />
      {/* The one figure here that IGNORES the picker: "החודש" means the
          calendar month we are standing in, always. Said in the detail
          line and again on hover, because a metric that quietly refuses
          the filter beside two that obey it is otherwise just wrong. */}
      <Metric
        // The month is IN the label, not implied by it: "חודש נוכחי"
        // reads relative to the period picker two lines above, which is
        // exactly what this figure ignores. Naming ספטמבר outright means
        // the label cannot be misread whatever the range says.
        label={`צפי הכנסות - חודש ${monthNameOf(summary.current_month)}`}
        value={formatCurrencyWhole(summary.current_month_revenue)}
        detail={`${monthLabelOf(summary.prev_month)}: ${formatCurrencyWhole(summary.prev_month_revenue)}`}
        title={`${monthLabelOf(summary.current_month)} — לפי התאריך של היום, לא לפי התקופה של שאר המדדים כאן. מתוכו כבר שולם ${formatCurrencyWhole(summary.current_month_collected)}.`}
      />
      {/* THE star metric (owner, 14/09/2026) — marked as one. The headline
          is PER MONTH (owner, same day): the span total grows with the range's
          length, so twelve months read four times better than three for the
          same business, and the delta was already measured on the monthly
          figure. The span total stays, as context, when the span is longer
          than one month — over a single month the two are the same number. */}
      {rpe && (
        <Metric
          tone="star"
          icon="star"
          label="הכנסות לעובד בחודש"
          value={rpe.monthly == null ? '—' : formatCurrencyWhole(rpe.monthly)}
          extra={rpe.value == null ? null : (
            <span className="cc-metric-detail">
              ממוצע {headcountLabel(rpe.avg_headcount)} עובדים
              {rpe.months > 1 && <> · {formatCurrencyWhole(rpe.value)} לעובד בכל התקופה</>}
            </span>
          )}
          detail={rpe.value == null ? PRELAUNCH_NOTE : <DeltaDetail
            now={rpe.monthly}
            prev={summary.revenue_per_employee_prev?.monthly || 0}
            prevLabel={prevLabel}
            prevPrelaunch={prevPrelaunch}
            formatValue={formatCurrencyWhole}
          />}
          menu={onEditHeadcount && (
            <CardHelp
              title="הכנסות לעובד בחודש"
              heading="איך מחושב ולמה זה חשוב"
              action={{ label: 'עריכת מספר עובדים בחודש', icon: 'users', run: onEditHeadcount }}
            >
              <HelpNote>
                <b>החישוב:</b> סה״כ ההכנסות בטווח (אותו מספר כמו "סה״כ הכנסות") חלקי
                <b> ממוצע מספר העובדים</b> בחודשי הטווח, חלקי <b>מספר החודשים</b>. מספר העובדים
                נקבע לכל חודש בנפרד; חודש שלא הוזן בו מספר נחשב 2 עובדים. חודשים לפני עליית PEAK
                לא נספרים.
              </HelpNote>
              <HelpNote>
                <b>למה לחודש:</b> כך המספר לא תלוי באורך הטווח — 3 חודשים ו-12 חודשים של אותו
                עסק מראים את אותו סדר גודל, וההשוואה לתקופה הקודמת תקפה. השורה שמתחת מראה את
                הסכום לכל התקופה. להשוואה לחברות אחרות מקובל המספר השנתי — בערך פי 12.
              </HelpNote>
              <HelpNote>
                <b>למה זה המדד המרכזי:</b> הוא מראה אם PEAK גדלה בלי שהעלות האנושית גדלה
                איתה. מוצר שמתרחב נכון מוסיף לקוחות והכנסות מהר יותר משהוא מוסיף אנשים — ואז
                המספר הזה עולה.
              </HelpNote>
              <HelpNote>
                כשהוא יורד, או שגייסנו לפני שההכנסות הגיעו, או שהלקוחות החדשים דורשים יותר
                עבודה ידנית מאיתנו (למשל אישור טיוטות) — שתי סיבות לעצור ולבדוק.
              </HelpNote>
            </CardHelp>
          )}
        />
      )}
    </div>
  );
}
